import { Select } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'
import { IssueRiskAspectSelect } from '@/components_deprecated/Select/IssueRiskAspectSelect'
import type { UserNode } from '@/modules/users'
import { Icon } from '@ur-solutions/react-components'
import type { SelectOption } from '@ur-solutions/react-components'
import type { FormErrors, FormOnChangeFn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { set } from 'date-fns'
import React, { useState } from 'react'
import styled from 'styled-components'
import type { ShallowCategory } from '.'
import { CategorySelect } from '.'
import {
  IssueRelationField,
  useIssueRelations,
} from './issue-relations-selector'
import { useIssueSeverityOptions } from '../consts'
import type { EditIssueForm } from '../types'
import { DatePicker, TimePicker } from '@/components'

const COLS = 3

const Wrapper = styled.section`
  grid-area: main;
  min-width: 0;
  max-width: 100%;

  display: grid;
  grid-template-columns: repeat(${COLS}, 1fr);

  span.unknown {
    color: ${props => props.theme.colors.red};
  }

  /* Uniform borders: every cell gets left/top borders, then suppress the
     first column's left border and the first row's top border. Works for
     any number of cells. */
  & > div {
    border-left: 1px solid ${props => props.theme.colors.gray8};
    border-top: 1px solid ${props => props.theme.colors.gray8};
  }
  & > div:nth-child(${COLS}n + 1) {
    border-left: 0;
  }
  & > div:nth-child(-n + ${COLS}) {
    border-top: 0;
  }

  & > div.date {
    grid-column: 1 / -1;
  }

  /* Long select labels truncate with ellipsis instead of wrapping; cells
     stay the same height regardless of option name length. The popup
     (".--select-curtain") is intentionally untouched so the full option
     names show when picking.

     The deprecated Select DOM is:
       .--select-display (flex container with display + arrow icon)
         .--select-display-inner (wraps the label span)
           .--select-display-selected (the label span)
         .--select-arrow-icon
     For ellipsis to work, every level needs to allow shrinking
     (min-width: 0) and the label span itself owns the overflow rules. */
  .--select-display {
    min-width: 0;
    max-width: 100%;
  }
  .--select-display-inner {
    min-width: 0;
    overflow: hidden;
    flex: 1 1 0%;
  }
  .--select-display-selected,
  .--select-placeholder {
    display: block;
    min-width: 0;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${props => props.theme.media.mobile} {
    display: flex;
    flex-direction: column;

    & > div {
      border: 1px solid ${props => props.theme.colors.gray8} !important;
      border-width: 1px 0 0 0 !important;

      &.category {
        order: -1;
        border-width: 0 !important;
      }

      &.date {
        order: -1;
      }
    }
  }
`
const Info = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  gap: 0.5rem;
  min-width: 0;

  & > span {
    font-weight: 600;
    align-items: center;
    display: flex;
    color: ${props => props.theme.colors.gray5};
  }

  p {
    margin: 0;
    font-size: 1.6em;
  }

  ${props => props.theme.media.mobile} {
    padding: 1rem;

    span {
      font-size: 1rem;
    }

    p {
      font-size: 1rem;
    }
  }
`
const Editor = styled.div`
  margin-top: 0.5rem;
  min-width: 0;
`
const DateEditor = styled(Editor)`
  display: flex;

  & > * + * {
    margin-left: 0.5rem;
  }
`
const TimeLabel = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${props => props.theme.colors.gray5};
`

const TimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

interface CreateMainInfoProps {
  form: EditIssueForm
  errors: FormErrors<EditIssueForm>
  updateForm: (values: Partial<EditIssueForm>) => void
  onChange: FormOnChangeFn<EditIssueForm>
  subcategoryOptions: SelectOption<string, any>[]
  subcategoriesLoading: boolean
  requireSeverity: boolean
  requireRiskAspect: boolean
}

const RequiredMark = () => <span style={{ color: 'red' }}>*</span>

export const CreateMainInfo: React.FC<CreateMainInfoProps> = ({
  form,
  errors,
  updateForm,
  onChange,
  subcategoryOptions,
  subcategoriesLoading,
  requireSeverity,
  requireRiskAspect,
}) => {
  const translations = useTranslate({
    category: 'common.category',
    responsible: 'common.responsible',
    caseHandler: 'common.case-handler',
    date: 'common.date',
    severity: 'common.severity',
    riskAspect: 'common.risk-aspect',
    subCategory: 'common.sub-category',
    select: 'common.select',
    none: 'common.none',
    dataFrom: 'common.data-from',
    dataTo: 'common.data-to',
    addRelation: 'issues.add-relation',
  })
  const severityOptions = useIssueSeverityOptions()
  const {
    visibleRelations,
    availableOptionalRelations,
    setRelationOption,
    addOptional,
    removeOptional,
  } = useIssueRelations(form.relationOptions, relationOptions =>
    updateForm({ relationOptions })
  )

  const [temporaryResponsible, setTemporaryResponsible] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)

  const [temporaryCaseHandler, setTemporaryCaseHandler] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)

  function handleChangeCategory(id: string, category: ShallowCategory | null) {
    updateForm({
      category: id,
      subcategory: null,
    })

    setTemporaryResponsible(category?.responsibleUser ?? null)
    setTemporaryCaseHandler(category?.caseHandler ?? null)
  }

  function handleChangeDate(date: Date, fieldName: keyof EditIssueForm) {
    const baseDate =
      form[fieldName] instanceof Date ? form[fieldName] : new Date()

    const newDate = set(date, {
      hours: baseDate.getHours(),
      minutes: baseDate.getMinutes(),
      seconds: baseDate.getSeconds(),
    })

    if (fieldName === 'date' && form.dateEnd && newDate > form.dateEnd) {
      onChange('dateEnd')(newDate)
    }

    onChange(fieldName)(newDate)
  }

  // TODO these should not exist
  // useEffect(() => {
  //   if (form?.date) {
  //     const newDateEnd = new Date(form.date.getTime())
  //     onChange('dateEnd')(newDateEnd)
  //   }
  // }, [form?.date])

  // useEffect(() => {
  //   onChange('subcategory')(null)
  // }, [form.category])

  return (
    <Wrapper>
      <Info className="category">
        <span>
          {translations.category} <RequiredMark />
        </span>
        <Editor>
          <CategorySelect
            value={form.category}
            error={errors.category}
            fullWidth
            placeholder={translations.select}
            onChange={handleChangeCategory}
          />
        </Editor>
        {form.category && subcategoryOptions.length > 0 && (
          <>
            <span>
              {translations.subCategory} <RequiredMark />
            </span>
            <Select
              loading={subcategoriesLoading}
              value={form.subcategory}
              options={subcategoryOptions}
              onChange={onChange('subcategory')}
              height="3rem"
              grayed
              fullWidth
              nullable
              error={errors.subcategory}
            />
          </>
        )}
      </Info>

      <Info>
        <span>{translations.responsible}</span>
        <p>
          {temporaryResponsible ? (
            <PlainLink
              to={`/users/${temporaryResponsible.id}`}
              $hoverColor="secondary"
            >
              {temporaryResponsible.fullName}
            </PlainLink>
          ) : (
            translations.none
          )}
        </p>
      </Info>

      <Info>
        <span>{translations.caseHandler}</span>
        <p>
          {temporaryCaseHandler ? (
            <PlainLink
              to={`/users/${temporaryCaseHandler.id}`}
              $hoverColor="secondary"
            >
              {temporaryCaseHandler.fullName}
            </PlainLink>
          ) : (
            translations.none
          )}
        </p>
      </Info>

      <Info>
        <span>
          {translations.severity} {requireSeverity && <RequiredMark />}
        </span>
        <Editor>
          <Select
            value={form.severity}
            options={severityOptions}
            onChange={onChange('severity')}
            height="3rem"
            grayed
            fullWidth
            nullable
          />
        </Editor>
      </Info>
      <Info>
        <span>
          {translations.riskAspect} {requireRiskAspect && <RequiredMark />}
        </span>
        <Editor>
          <IssueRiskAspectSelect
            value={form.riskAspect}
            onChange={onChange('riskAspect')}
            height="3rem"
            fullWidth
            grayed
            nullable
          />
        </Editor>
      </Info>
      {visibleRelations.map(relation => (
        <Info key={relation.id}>
          <span>
            {relation.name}
            {relation.required ? (
              <span style={{ color: 'red' }}>*</span>
            ) : (
              <Icon
                icon="times"
                size="lg"
                cursor="pointer"
                color="gray6"
                hoverColor="red"
                margin="0 0 0 auto"
                onClick={() => removeOptional(relation.id)}
                aria-label={translations.select}
              />
            )}
          </span>
          <Editor>
            <IssueRelationField
              relation={relation}
              value={form.relationOptions[relation.id] ?? null}
              onChange={id => setRelationOption(relation.id, id)}
            />
          </Editor>
        </Info>
      ))}

      {availableOptionalRelations.length > 0 && (
        <Info>
          <span>{translations.addRelation}</span>
          <Editor>
            <Select
              value={null}
              options={availableOptionalRelations.map(r => ({
                label: r.name,
                value: r.id,
              }))}
              onChange={(id: string | null) => id && addOptional(id)}
              placeholder={translations.addRelation}
              fullWidth
              grayed
              height="3rem"
              nullable
            />
          </Editor>
        </Info>
      )}

      {/* Filler cells so the last row before .date is always full width.
          Pre-relation cells: 5 (category, responsible, caseHandler, severity,
          riskAspect). Plus visible relations and the "Add" picker (if shown). */}
      {Array.from({
        length:
          (3 -
            ((5 +
              visibleRelations.length +
              (availableOptionalRelations.length > 0 ? 1 : 0)) %
              3)) %
          3,
      }).map((_, i) => (
        <Info key={`filler-${i}`} aria-hidden />
      ))}
      <Info className="date">
        <TimeContainer>
          <div>
            <TimeLabel>{translations.dataFrom}</TimeLabel>
            <DateEditor>
              <TimePicker value={form.date} onChange={onChange('date')} />
              <DatePicker
                mode="single"
                value={form.date}
                onChange={date =>
                  date ? handleChangeDate(date, 'date') : undefined
                }
              />
            </DateEditor>
          </div>
          <div>
            <TimeLabel>{translations.dataTo}</TimeLabel>
            <DateEditor>
              <TimePicker value={form.dateEnd} onChange={onChange('dateEnd')} />
              <DatePicker
                mode="single"
                value={form.dateEnd}
                onChange={date =>
                  date ? handleChangeDate(date, 'dateEnd') : undefined
                }
              />
            </DateEditor>
          </div>
        </TimeContainer>
      </Info>
    </Wrapper>
  )
}
